import classNames from "classnames/bind";
import { clamp } from "es-toolkit/compat";
import React from "react";
import { Button } from "../../Button";
import { colours } from "../../constants/colours";
import { Icon } from "../../Icon";
import { Popover } from "../../Popover";
import { DataDisplayWithAdornment } from "../_components/DataDisplayWithAdornment";
import styles from "./ArrayDataDisplay.module.scss";
import type { ArrayDataDisplayProps } from "./types";

const cx = classNames.bind(styles);

const ArrayDataDisplay = ({
  value,
  maxValuesToPreview = 3,
  alignment,
  isInline,
  isBusy,
  isLoading,
  emptyText = "-",
  showInvalidIndicator = true,
  invalidText = "Unsupported data type",
  className,
  style,
  as = "ul",
  valueAs = "li",
  valueSeparator = ", ",
}: ArrayDataDisplayProps) => {
  const isEmpty =
    (Array.isArray(value) && !value?.length) ||
    value === null ||
    value === undefined;
  const isInvalid = !Array.isArray(value) && value !== null;
  const valuesToShow =
    value?.slice(
      0,
      clamp(
        Math.round(maxValuesToPreview),
        1,
        !isEmpty ? value.length : Math.abs(Math.round(maxValuesToPreview))
      )
    ) ?? [];
  const valueToRender = !isInvalid ? (value ?? []) : [];
  const ValueCustomNode = valueAs ?? "li";

  return (
    <DataDisplayWithAdornment
      showAdornment={showInvalidIndicator && !isEmpty && isInvalid}
      alignment={alignment}
      isInline={isInline}
      isBusy={isBusy}
      isLoading={isLoading}
      adornmentSlot={() =>
        !isEmpty && isInvalid ? (
          <Icon
            variant="warning"
            colour={colours.status.warning}
            size={12}
            alt={invalidText}
            aria-roledescription="Warning Icon"
          />
        ) : null
      }
      className={cx("container", className)}
      style={style}
      as={as}
    >
      {isEmpty || isInvalid ? (
        emptyText
      ) : valuesToShow.length < valueToRender.length ? (
        <>
          {valuesToShow.map((v, idx) => (
            <ValueCustomNode
              className={cx("list-item")}
              key={`${String(v)}-${idx}`}
            >
              {v}
              {idx < valuesToShow.length - 1 ? valueSeparator : ""}
            </ValueCustomNode>
          ))}
          <Popover
            contentRender={() => (
              <div className={cx("content-container")}>
                <ul className={cx("content-list")}>
                  {valueToRender.map((item) => (
                    <li
                      key={item?.toString()}
                      tabIndex={0}
                      className={cx("content-list-item")}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            contentClassName={cx("content-container")}
            shouldDisplayArrow={false}
          >
            <li className={cx("list-item")}>
              <Button
                variant="text"
                size="xx-small"
                className={cx("preview-trigger")}
              >
                +{valueToRender.length - valuesToShow.length} more
              </Button>
            </li>
          </Popover>
        </>
      ) : (
        valueToRender.map((v, idx) => (
          <ValueCustomNode
            className={cx("list-item")}
            key={`${String(v)}-${idx}`}
          >
            {v}
            {idx < valueToRender.length - 1 ? valueSeparator : ""}
          </ValueCustomNode>
        ))
      )}
    </DataDisplayWithAdornment>
  );
};

export default ArrayDataDisplay;
